import {AxiosResponseHeaders} from "axios";

/**
 * A file the API returns: its content and the name to download it as.
 */
export interface DownloadedFile {
  filename: string;
  content: Blob;
}

/**
 * Reads the filename from a `Content-Disposition` header, preferring the UTF-8 encoded `filename*` over the plain
 * `filename`, and falls back to `fallback` when the header names none.
 */
export function toFilename(contentDisposition: string | undefined, fallback: string): string {
  const encoded = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (encoded) {
    return decodeURIComponent(encoded[1]);
  }
  const plain = contentDisposition?.match(/filename="?([^";]+)"?/i);
  return plain ? plain[1] : fallback;
}

/**
 * Keeps a successful binary response as it is, but parses a JSON error body, so the global error handling can read
 * a problem's `title` as for any other request.
 */
export function parseProblemBody(data: ArrayBuffer, headers: AxiosResponseHeaders, status?: number): unknown {
  if (status !== undefined && status >= 400 && data.byteLength > 0
    && String(headers["content-type"]).includes("json")) {
    return JSON.parse(new TextDecoder().decode(data));
  }
  return data;
}

/**
 * Saves a downloaded file in the browser under its filename.
 */
export function saveFile(file: DownloadedFile): void {
  const url = window.URL.createObjectURL(file.content);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", file.filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
